import * as Sentry from '@sentry/nextjs';
import { shouldSendSentry } from '@/lib/environment';
import { applySentryBaseScope } from '@/lib/sentryLogs';

const SENTRY_REPORTED_FLAG = '__museinSentryReported';

function markSentryReported(error: unknown) {
  if (!error || (typeof error !== 'object' && typeof error !== 'function')) {
    return;
  }

  try {
    Reflect.set(error, SENTRY_REPORTED_FLAG, true);
  } catch {
    // Ignore non-extensible error objects.
  }
}

export function isSentryReported(error: unknown) {
  if (!error || (typeof error !== 'object' && typeof error !== 'function')) {
    return false;
  }

  return Reflect.get(error, SENTRY_REPORTED_FLAG) === true;
}

function withReportingGuard(error: unknown, callback: () => void) {
  if (!shouldSendSentry()) {
    return;
  }

  if (isSentryReported(error)) {
    return;
  }

  callback();
  markSentryReported(error);
}

export interface ApiErrorReportContext {
  url: string;
  method: string;
  language: string;
  traceId: string;
  silent: boolean;
  status?: number;
  code?: string | number;
  data?: unknown;
  request?: Record<string, unknown>;
  response?: Record<string, unknown>;
}

function serializeSentryJson(value: unknown) {
  if (value === undefined) {
    return undefined;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '[Unserializable JSON]';
  }
}

function extractCanvasGenerationFailedTaskMessages(error: unknown) {
  if (!error || typeof error !== 'object') {
    return [];
  }

  const rawFailedTasks = Reflect.get(error, 'failedTasks');
  if (!Array.isArray(rawFailedTasks)) {
    return [];
  }

  return rawFailedTasks.filter((task) => {
    if (!task || typeof task !== 'object') {
      return false;
    }

    const message = Reflect.get(task, 'message');
    return typeof message === 'string' && message.length > 0;
  });
}

export function reportApiErrorToSentry(error: unknown, context: ApiErrorReportContext) {
  withReportingGuard(error, () => {
    Sentry.withScope((scope) => {
      applySentryBaseScope(scope);
      scope.setTag('error_domain', 'api');
      scope.setTag('api_url', context.url);
      scope.setTag('api_method', context.method);
      scope.setTag('api_status', String(context.status ?? 'unknown'));
      scope.setTag('api_code', String(context.code ?? 'unknown'));
      scope.setTag('api_silent', String(context.silent));
      scope.setContext('api', {
        url: context.url,
        method: context.method,
        language: context.language,
        traceId: context.traceId,
        status: context.status,
        code: context.code,
        silent: context.silent,
        data: context.data,
      });
      if (context.request) {
        scope.setContext('api_request', context.request);
        scope.setExtra('api_request_json', serializeSentryJson(context.request));
      }
      if (context.response) {
        scope.setContext('api_response', context.response);
        scope.setExtra('api_response_json', serializeSentryJson(context.response));
      }
      if (context.data !== undefined) {
        scope.setExtra('api_data_json', serializeSentryJson(context.data));
      }
      Sentry.captureException(error);
    });
  });
}

export interface LoginErrorReportContext {
  action: 'missing_token' | 'google_init' | 'google_decode' | 'auth_request' | 'state_sync';
  method: 'google';
  hasInviteCode: boolean;
  inviteCodeSource: 'url' | 'dialog' | 'none';
  googleUserId?: string;
  googleUserEmail?: string;
  authErrorCode?: string | number;
  authUserId?: string;
}

export function reportLoginErrorToSentry(error: unknown, context: LoginErrorReportContext) {
  withReportingGuard(error, () => {
    Sentry.withScope((scope) => {
      applySentryBaseScope(scope);
      scope.setTag('error_domain', 'login');
      scope.setTag('login_method', context.method);
      scope.setTag('login_action', context.action);
      scope.setTag('has_invite_code', String(context.hasInviteCode));
      scope.setTag('invite_code_source', context.inviteCodeSource);
      if (context.authErrorCode !== undefined) {
        scope.setTag('auth_error_code', String(context.authErrorCode));
      }
      scope.setContext('login', {
        action: context.action,
        method: context.method,
        hasInviteCode: context.hasInviteCode,
        inviteCodeSource: context.inviteCodeSource,
        googleUserId: context.googleUserId,
        googleUserEmail: context.googleUserEmail,
        authErrorCode: context.authErrorCode,
        authUserId: context.authUserId,
      });
      Sentry.captureException(error);
    });
  });
}

export interface CanvasGenerationErrorReportContext {
  action:
    | 'batch_process'
    | 'batch_process_missing_task_ids'
    | 'batch_query'
    | 'batch_query_timeout'
    | 'batch_query_all_failed'
    | 'batch_query_resume';
  canvasId?: string;
  nodeId?: string;
  nodeType?: string;
  modelId?: string;
  modelType?: string;
  requestType?: string;
  taskIds?: string[];
  retryCount?: number;
  failedCount?: number;
  successCount?: number;
  totalTaskCount?: number;
  outputCount?: number;
  extra?: Record<string, unknown>;
}

export function reportCanvasGenerationErrorToSentry(
  error: unknown,
  context: CanvasGenerationErrorReportContext,
) {
  withReportingGuard(error, () => {
    Sentry.withScope((scope) => {
      applySentryBaseScope(scope);
      const failedTaskMessages = extractCanvasGenerationFailedTaskMessages(error);

      scope.setTag('error_domain', 'infinite_canvas');
      scope.setTag('canvas_action', context.action);
      if (context.nodeType) {
        scope.setTag('node_type', context.nodeType);
      }
      if (context.modelType) {
        scope.setTag('model_type', context.modelType);
      }
      if (context.modelId) {
        scope.setTag('model_id', context.modelId);
      }
      if (context.action === 'batch_query_timeout') {
        scope.setTag('query_timeout', 'true');
      }
      scope.setContext('canvas_generation', {
        action: context.action,
        canvasId: context.canvasId,
        nodeId: context.nodeId,
        nodeType: context.nodeType,
        modelId: context.modelId,
        modelType: context.modelType,
        requestType: context.requestType,
        taskIds: context.taskIds,
        retryCount: context.retryCount,
        failedCount: context.failedCount,
        successCount: context.successCount,
        totalTaskCount: context.totalTaskCount,
        outputCount: context.outputCount,
        failedTasks: failedTaskMessages,
        ...context.extra,
      });
      if (failedTaskMessages.length > 0) {
        scope.setExtra('canvas_generation_failed_tasks_json', serializeSentryJson(failedTaskMessages));
      }
      Sentry.captureException(error);
    });
  });
}

export interface UploadErrorReportContext {
  runtime: 'browser' | 'node';
  stage: 'prepare' | 'presign_request' | 'presign_response' | 'direct_upload';
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  nodeType?: string;
  folder?: string;
  uploadUrl?: string;
  endpoint?: string;
  provider?: string;
  status?: number;
  method?: string;
  contentHash?: string;
  extra?: Record<string, unknown>;
}

export function reportUploadErrorToSentry(
  error: unknown,
  context: UploadErrorReportContext,
) {
  withReportingGuard(error, () => {
    Sentry.withScope((scope) => {
      applySentryBaseScope(scope);
      scope.setTag('error_domain', 'upload');
      scope.setTag('upload_runtime', context.runtime);
      scope.setTag('upload_stage', context.stage);
      if (context.nodeType) {
        scope.setTag('upload_node_type', context.nodeType);
      }
      if (context.provider) {
        scope.setTag('upload_provider', context.provider);
      }
      if (context.status !== undefined) {
        scope.setTag('upload_status', String(context.status));
      }
      if (context.method) {
        scope.setTag('upload_method', context.method);
      }
      scope.setContext('upload', {
        runtime: context.runtime,
        stage: context.stage,
        fileName: context.fileName,
        fileType: context.fileType,
        fileSize: context.fileSize,
        nodeType: context.nodeType,
        folder: context.folder,
        uploadUrl: context.uploadUrl,
        endpoint: context.endpoint,
        provider: context.provider,
        status: context.status,
        method: context.method,
        contentHash: context.contentHash,
        ...context.extra,
      });
      Sentry.captureException(error);
    });
  });
}

export function buildReportableError(message: string, extras?: Record<string, unknown>) {
  const error = new Error(message) as Error & Record<string, unknown>;
  if (extras) {
    Object.assign(error, extras);
  }
  return error;
}
